import React, { useMemo } from "react"
import { Thread, ThreadEvent, ThreadMessage } from "../../../../types"
import EventItem from "../event"
import Message from "../message"

// Type guard to check if message is a ThreadMessage
function isEvent(message: ThreadMessage | ThreadEvent): message is ThreadEvent {
  return "type" in message
}

interface Props {
  conversation: (ThreadMessage | ThreadEvent)[]
  item: ThreadMessage | ThreadEvent
  thread: Thread
  index: number
}

const ThreadItem = React.memo<Props>(
  ({ conversation, item, thread, index }: Props) => {
    // Memoize expensive calculations - moved outside conditional to fix hooks rule
    const messageProps = useMemo(() => {
      if (isEvent(item)) {
        return null // No message props needed for events
      }

      const initials = `${thread.first_name[0]}${thread.last_name[0]}`
      const nextMessage = conversation[index + 1]

      const nextIsEvent = nextMessage && isEvent(nextMessage)
      const nextMsgIsSameUser =
        !!nextMessage && !nextIsEvent && nextMessage.sender === item.sender

      // if nextMsgIsSameUser check timestamp difference
      // if the difference is less than 1 minutes, don't show timestamp
      // if the difference is greater than 1 minute, show timestamp
      const nextMsgTimestamp = nextMessage
        ? new Date(nextMessage.timestamp)
        : null
      const currentMsgTimestamp = new Date(item.timestamp)
      const diff = nextMsgTimestamp
        ? nextMsgTimestamp.getTime() - currentMsgTimestamp.getTime()
        : 0
      const nextMsgIsSameUserAndWithin1Minute =
        nextMsgIsSameUser && diff < 60000

      return {
        initials,
        showTimestamp: !nextMsgIsSameUserAndWithin1Minute,
        nextMsgIsSameUser
      }
    }, [conversation, item, thread, index])

    if (isEvent(item)) {
      return <EventItem key={thread._id} event={item} />
    }

    return (
      <Message
        showTimestamp={messageProps!.showTimestamp}
        key={item._id}
        message={item}
        initials={messageProps!.initials}
        nextMsgIsSameUser={messageProps!.nextMsgIsSameUser}
      />
    )
  }
)

ThreadItem.displayName = "ThreadItem"

export default ThreadItem
